import { useMemo } from 'react';
import { coerceNumber } from '@visx/scale';
function animatedValue(animationTrajectory, positionOnScale, scaleMin, scaleMax, scaleHalfwayPoint) {
  switch (animationTrajectory) {
    case 'center':
      return scaleHalfwayPoint;
    case 'min':
      return scaleMin ?? 0;
    case 'max':
      return scaleMax ?? 0;
    case 'outside':
    default:
      return ((positionOnScale ?? 0) < scaleHalfwayPoint ? scaleMin : scaleMax) ?? 0;
  }
}
function enterUpdate(_ref) {
  let {
    from,
    to
  } = _ref;
  return {
    fromX: from.x,
    toX: to.x,
    fromY: from.y,
    toY: to.y,
    opacity: 1
  };
}
/**
 * A hook that returns `react-spring` transition config for animating a Line
 * horizontally, vertically, and from a specific starting point.
 */
export default function useLineTransitionConfig(_ref2) {
  let {
    scale,
    animateXOrY,
    animationTrajectory: initAnimationTrajectory = 'outside'
  } = _ref2;
  const shouldAnimateX = animateXOrY === 'x';
  return useMemo(() => {
    const [a, b] = scale.range().map(coerceNumber);
    const isDescending = b != null && a != null && b < a;
    const [scaleMin, scaleMax] = isDescending ? [b, a] : [a, b];
    const scaleLength = b != null && a != null ? Math.abs(b - a) : 0;
    const scaleHalfwayPoint = (scaleMin ?? 0) + scaleLength / 2;
    let animationTrajectory = initAnimationTrajectory;

    // correct direction for y-axis which is inverted due to svg coords
    if (!shouldAnimateX && initAnimationTrajectory === 'min') animationTrajectory = 'max';
    if (!shouldAnimateX && initAnimationTrajectory === 'max') animationTrajectory = 'min';
    const fromLeave = _ref3 => {
      let {
        from,
        to
      } = _ref3;
      return {
        fromX: shouldAnimateX ? animatedValue(animationTrajectory, from.x, scaleMin, scaleMax, scaleHalfwayPoint) : from.x,
        toX: shouldAnimateX ? animatedValue(animationTrajectory, from.x, scaleMin, scaleMax, scaleHalfwayPoint) : to.x,
        fromY: shouldAnimateX ? from.y : animatedValue(animationTrajectory, from.y, scaleMin, scaleMax, scaleHalfwayPoint),
        toY: shouldAnimateX ? to.y : animatedValue(animationTrajectory, from.y, scaleMin, scaleMax, scaleHalfwayPoint),
        opacity: 0
      };
    };
    return {
      from: fromLeave,
      leave: fromLeave,
      enter: enterUpdate,
      update: enterUpdate
    };
  }, [scale, shouldAnimateX, initAnimationTrajectory]);
}